import React, { useMemo } from 'react';
import dataService from '../services/dataService';
import stateGovernmentService from '../services/stateGovernmentService';

// Colorblind-friendly palette
const COLORS = {
    primary: '#0072B2',     // Blue
    secondary: '#D55E00',   // Vermillion  
    success: '#009E73',     // Bluish green
    warning: '#F0E442',     // Yellow
    danger: '#CC79A7',      // Reddish purple
    neutral: '#999999',     // Gray
    accent: '#56B4E9',      // Sky blue
    dark: '#000000',        // Black
};

const StateDataVisualizations = ({ selectedState }) => {
    const stateName = selectedState ? dataService.getStateName(selectedState) : null;
    const stateData = selectedState ? dataService.getStateData(selectedState) : null;
    const govData = selectedState ? stateGovernmentService.getStateGovernment(selectedState) : null;
    const nationalAvg = dataService.getNationalAverages();
    
    // Calculate comparison metrics
    const metrics = useMemo(() => {
        if (!stateData || !selectedState) return null;
        
        return {
            // Demographics
            population: stateData.population || 0,
            populationDensity: stateData.populationDensity || 0,
            medianAge: stateData.medianAge || 0,
            
            // Economics  
            medianIncome: stateData.medianHouseholdIncome || 0,
            gdpPerCapita: stateData.gdpPerCapita || 0,
            povertyRate: stateData.povertyRate || 0,
            unemploymentRate: stateData.unemploymentRate || 0,
            
            // Housing
            medianHomeValue: stateData.medianHomeValue || 0,
            medianRent: stateData.medianRent || 0,
            homeownershipRate: stateData.homeownershipRate || 0,
            
            // Education & Health
            educationBachelor: stateData.educationBachelor || 0,
            highSchoolGraduation: stateData.highSchoolGraduation || 0,
            uninsuredRate: stateData.uninsuredRate || 0,
            lifeExpectancy: stateData.lifeExpectancy || 0,
            
            // Crime
            violentCrimeRate: stateData.violentCrimeRate || 0,
            propertyCrimeRate: stateData.propertyCrimeRate || 0,
            
            // Cost & Quality
            costOfLivingIndex: stateData.costOfLivingIndex || 100,
            salesTaxRate: stateData.salesTaxRate || 0,
            stateIncomeTaxRate: stateData.stateIncomeTaxRate || 0,
            averageCommuteTime: stateData.averageCommuteTime || 0,
            broadbandAvailability: stateData.broadbandAvailability || 0,
            airQualityIndex: stateData.airQualityIndex || 0,
            
            // Political
            governorParty: govData?.governor?.party || 'N/A',
            senatorParties: govData?.senators?.map(s => s.party) || [],
            avgPoliticalAge: govData?.ageStatistics?.averageAge || 0
        };
    }, [stateData, govData, selectedState]);
    
    if (!selectedState || !metrics) return null;
    
    // Component styles - simplified for clarity
    const containerStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
        gap: '20px',
        padding: '20px',
        backgroundColor: '#FAFBFC',
        marginTop: '20px'
    };
    
    const chartCardStyle = {
        backgroundColor: 'white',
        borderRadius: '6px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    };
    
    // Simplified Horizontal Bar Chart with clear comparisons
    const ComparisonBar = ({ data }) => {
        const validData = data.filter(d => d.value != null && !isNaN(d.value));
        const allValues = [...validData.map(d => d.value), ...validData.map(d => d.benchmark || 0).filter(b => b > 0)];
        const maxValue = Math.max(...allValues, 1);
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {data.map((item, index) => {
                    const value = Number(item.value) || 0;
                    const benchmark = Number(item.benchmark) || 0;
                    const isValid = !isNaN(value) && value != null;
                    const hasBenchmark = !isNaN(benchmark) && benchmark > 0;
                    
                    // Format values for display
                    const formatValue = (val) => {
                        if (item.format === 'percent') return `${val.toFixed(1)}%`;
                        if (item.format === 'currency') return `$${(val/1000).toFixed(0)}K`;
                        return val.toLocaleString();
                    };
                    
                    // Determine performance color
                    const performanceColor = () => {
                        if (!hasBenchmark) return COLORS.primary;
                        const better = item.higherIsBetter ? value > benchmark : value < benchmark;
                        return better ? COLORS.success : COLORS.secondary;
                    };
                    
                    return (
                        <div key={index}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                marginBottom: '6px',
                                alignItems: 'baseline'
                            }}>
                                <span style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>
                                    {item.label}
                                </span>
                                <div style={{ fontSize: '13px', color: '#666' }}>
                                    {!isValid ? 'No data' : (
                                        <>
                                            <span style={{ fontWeight: '600', color: performanceColor() }}>
                                                {formatValue(value)}
                                            </span>
                                            {hasBenchmark && (
                                                <span style={{ marginLeft: '8px', color: '#999' }}>
                                                    (US: {formatValue(benchmark)})
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div style={{ position: 'relative', height: '20px' }}>
                                {/* Background */}
                                <div style={{ 
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#F5F5F5',
                                    borderRadius: '2px'
                                }} />
                                
                                {/* State value bar */}
                                {isValid && (
                                    <div style={{
                                        position: 'absolute',
                                        width: `${Math.min((value / maxValue) * 100, 100)}%`,
                                        height: '100%',
                                        backgroundColor: performanceColor(),
                                        borderRadius: '2px',
                                        opacity: 0.8,
                                        transition: 'width 0.3s ease'
                                    }} />
                                )}
                                
                                {/* US Average marker */}
                                {hasBenchmark && (
                                    <div style={{
                                        position: 'absolute',
                                        left: `${Math.min((benchmark / maxValue) * 100, 100)}%`,
                                        top: '-2px',
                                        bottom: '-2px',
                                        width: '2px',
                                        backgroundColor: '#333',
                                        opacity: 0.5
                                    }} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    
    // Simplified dot plot for distributions
    const DistributionDots = ({ data }) => {
        const maxValue = Math.max(...data.map(d => d.value).filter(v => !isNaN(v)), 1);
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {data.map((item, index) => {
                    const value = Number(item.value) || 0;
                    const isValid = !isNaN(value) && value != null;
                    
                    return (
                        <div key={index} style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '140px 1fr 80px',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                                {item.label}
                            </span>
                            <div style={{ 
                                height: '8px',
                                backgroundColor: '#F0F0F0',
                                borderRadius: '4px',
                                position: 'relative'
                            }}>
                                {isValid && (
                                    <div style={{
                                        position: 'absolute',
                                        left: `${(value / maxValue) * 100}%`,
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: item.color || COLORS.primary,
                                        borderRadius: '50%',
                                        border: '2px solid white',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                )}
                            </div>
                            <span style={{ 
                                fontSize: '13px', 
                                fontWeight: '500',
                                textAlign: 'right',
                                color: isValid ? '#333' : '#999'
                            }}>
                                {!isValid ? 'N/A' :
                                 item.format === 'percent' ? `${value.toFixed(1)}%` :
                                 item.format === 'currency' ? `$${(value/1000).toFixed(0)}K` :
                                 value.toFixed(1)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };
    
    // Key Performance Indicator card
    const KPICard = ({ value, label, rank, total = 50, color, format = 'number' }) => {
        const numValue = Number(value) || 0;
        const isValid = !isNaN(numValue) && numValue !== 0;
        
        const formatDisplay = () => {
            if (!isValid) return 'N/A';
            if (format === 'currency') return `$${(numValue/1000).toFixed(0)}K`;
            if (format === 'percent') return `${numValue.toFixed(1)}%`;
            if (format === 'years') return `${numValue.toFixed(1)} yrs`;
            return numValue.toLocaleString();
        };
        
        const getRankColor = () => {
            if (!rank) return '#999';
            const percentile = (total - rank + 1) / total;
            if (percentile >= 0.8) return COLORS.success;
            if (percentile >= 0.6) return COLORS.primary;
            if (percentile >= 0.4) return COLORS.warning;
            return COLORS.danger;
        };
        
        return (
            <div style={{
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #E0E0E0',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
            }}>
                <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: color || '#333' }}>
                    {formatDisplay()}
                </div>
                {rank && (
                    <div style={{ fontSize: '12px', color: getRankColor(), fontWeight: '500' }}>
                        Rank #{rank} of {total}
                    </div>
                )}
            </div>
        );
    };
    
    // Political party distribution - simplified
    const PartyDistribution = ({ data }) => {
        const total = data.reduce((sum, item) => sum + item.count, 0);
        if (total === 0) return <div>No data available</div>;
        
        const partyColors = {
            'Democrat': '#0072B2',
            'Republican': '#D55E00',
            'Independent': '#999999'
        };
        
        return (
            <div>
                <div style={{ 
                    display: 'flex',
                    height: '32px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                }}>
                    {data.filter(item => item.count > 0).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                width: `${(item.count / total) * 100}%`,
                                backgroundColor: partyColors[item.label] || '#666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {item.count}
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    {data.filter(item => item.count > 0).map((item, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: partyColors[item.label] || '#666',
                                borderRadius: '2px'
                            }} />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                {item.label} ({((item.count / total) * 100).toFixed(0)}%)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    
    // Gauge chart for single metric vs benchmark
    const SimpleGauge = ({ value, benchmark, label, format = 'number', higherIsBetter = true }) => {
        const numValue = Number(value) || 0;
        const numBenchmark = Number(benchmark) || 0;
        
        const performance = numBenchmark > 0 ? ((numValue - numBenchmark) / numBenchmark * 100) : 0;
        const isOutperforming = higherIsBetter ? performance > 0 : performance < 0;
        
        const formatDisplay = (val) => {
            if (format === 'currency') return `$${(val/1000).toFixed(0)}K`;
            if (format === 'percent') return `${val.toFixed(1)}%`;
            return val.toLocaleString();
        };
        
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: isOutperforming ? COLORS.success : COLORS.secondary }}>
                    {formatDisplay(numValue)}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {label}
                </div>
                {numBenchmark > 0 && (
                    <div style={{ 
                        fontSize: '13px', 
                        color: isOutperforming ? COLORS.success : COLORS.secondary,
                        marginTop: '8px',
                        fontWeight: '500'
                    }}>
                        {Math.abs(performance).toFixed(1)}% {performance > 0 ? 'above' : 'below'} US average
                    </div>
                )}
            </div>
        );
    };
    
    return (
        <div>
            {/* Insight-driven title */}
            <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#1a1a1a',
                marginBottom: '24px',
                textAlign: 'center'
            }}>
                {stateName}: {metrics.medianIncome > nationalAvg.medianHouseholdIncome ? 'Above' : 'Below'} National Average Income
            </h2>
            
            {/* Key Performance Indicators - Most important metrics at a glance */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '12px',
                marginBottom: '24px'
            }}>
                <KPICard 
                    value={metrics.medianIncome}
                    label="Median Income"
                    rank={dataService.getStateRank(selectedState, 'medianHouseholdIncome')}
                    color={COLORS.primary}
                    format="currency"
                />
                <KPICard 
                    value={metrics.unemploymentRate}
                    label="Unemployment"
                    rank={dataService.getStateRank(selectedState, 'unemploymentRate', true)}
                    color={metrics.unemploymentRate < nationalAvg.unemploymentRate ? COLORS.success : COLORS.secondary}
                    format="percent"
                />
                <KPICard 
                    value={metrics.educationBachelor}
                    label="College Educated"
                    rank={dataService.getStateRank(selectedState, 'educationBachelor')}
                    color={COLORS.accent}
                    format="percent"
                />
                <KPICard 
                    value={metrics.violentCrimeRate}
                    label="Crime Rate"
                    rank={dataService.getStateRank(selectedState, 'violentCrimeRate', true)}
                    color={metrics.violentCrimeRate < nationalAvg.violentCrimeRate ? COLORS.success : COLORS.danger}
                    format="number"
                />
                <KPICard 
                    value={metrics.costOfLivingIndex}
                    label="Cost of Living"
                    rank={dataService.getStateRank(selectedState, 'costOfLivingIndex', true)}
                    color={metrics.costOfLivingIndex < 100 ? COLORS.success : COLORS.warning}
                    format="number"
                />
            </div>
            
            <div style={containerStyle}>
                {/* Economic Health - Most critical metrics */}
                <div style={chartCardStyle}>
                    <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Economic Health: {metrics.unemploymentRate < nationalAvg.unemploymentRate ? 'Strong' : 'Challenged'}
                    </h3>
                    <ComparisonBar 
                        data={[
                            { 
                                label: 'Household Income',
                                value: metrics.medianIncome,
                                format: 'currency',
                                benchmark: nationalAvg.medianHouseholdIncome,
                                higherIsBetter: true
                            },
                            { 
                                label: 'GDP Per Capita',
                                value: metrics.gdpPerCapita,
                                format: 'currency',
                                benchmark: nationalAvg.gdpPerCapita,
                                higherIsBetter: true
                            },
                            { 
                                label: 'Poverty Rate',
                                value: metrics.povertyRate,
                                format: 'percent',
                                benchmark: nationalAvg.povertyRate,
                                higherIsBetter: false
                            },
                            { 
                                label: 'Unemployment',
                                value: metrics.unemploymentRate,
                                format: 'percent',
                                benchmark: nationalAvg.unemploymentRate,
                                higherIsBetter: false
                            }
                        ]}
                    />
                    <div style={{ 
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: '#F8F9FA',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        💡 Lower bars are better for poverty and unemployment rates
                    </div>
                </div>
                
                {/* Education & Workforce Quality */}
                <div style={chartCardStyle}>
                    <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Workforce Quality Indicators
                    </h3>
                    <DistributionDots 
                        data={[
                            { 
                                label: "Bachelor's Degree+",
                                value: metrics.educationBachelor,
                                format: 'percent',
                                color: COLORS.primary
                            },
                            { 
                                label: 'HS Graduation Rate',
                                value: metrics.highSchoolGraduation,
                                format: 'percent',
                                color: COLORS.accent
                            },
                            { 
                                label: 'Life Expectancy',
                                value: metrics.lifeExpectancy,
                                format: 'years',
                                color: COLORS.success
                            },
                            { 
                                label: 'Uninsured Rate',
                                value: metrics.uninsuredRate,
                                format: 'percent',
                                color: COLORS.secondary
                            }
                        ]}
                    />
                </div>
                
                {/* Housing Affordability Analysis */}
                <div style={chartCardStyle}>
                    <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Housing Market: {metrics.medianHomeValue / metrics.medianIncome > 4 ? 'Less Affordable' : 'More Affordable'}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <SimpleGauge 
                            value={metrics.medianHomeValue}
                            benchmark={nationalAvg.medianHomeValue}
                            label="Home Value"
                            format="currency"
                            higherIsBetter={false}
                        />
                        <SimpleGauge 
                            value={metrics.medianRent}
                            benchmark={nationalAvg.medianRent}
                            label="Monthly Rent"
                            format="currency"
                            higherIsBetter={false}
                        />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                            Homeownership Rate
                        </div>
                        <ComparisonBar 
                            data={[
                                { 
                                    label: 'Homeowners',
                                    value: metrics.homeownershipRate,
                                    format: 'percent',
                                    benchmark: nationalAvg.homeownershipRate,
                                    higherIsBetter: true
                                }
                            ]}
                        />
                    </div>
                    <div style={{ 
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#666',
                        textAlign: 'center'
                    }}>
                        Price-to-Income Ratio: {(metrics.medianHomeValue / metrics.medianIncome).toFixed(1)}x
                    </div>
                </div>
                
                {/* Safety Comparison */}
                <div style={chartCardStyle}>
                    <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Public Safety: {metrics.violentCrimeRate < nationalAvg.violentCrimeRate ? '✓ Safer than average' : '⚠ Higher crime rates'}
                    </h3>
                    <ComparisonBar 
                        data={[
                            { 
                                label: 'Violent Crime (per 100K)',
                                value: metrics.violentCrimeRate,
                                format: 'number',
                                benchmark: nationalAvg.violentCrimeRate,
                                higherIsBetter: false
                            },
                            { 
                                label: 'Property Crime (per 100K)',
                                value: metrics.propertyCrimeRate,
                                format: 'number',
                                benchmark: nationalAvg.propertyCrimeRate,
                                higherIsBetter: false
                            }
                        ]}
                    />
                    <div style={{ 
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: metrics.violentCrimeRate < nationalAvg.violentCrimeRate ? '#E8F5E9' : '#FFEBEE',
                        borderRadius: '4px',
                        fontSize: '13px'
                    }}>
                        Crime rates are {metrics.violentCrimeRate < nationalAvg.violentCrimeRate ? 
                            `${((nationalAvg.violentCrimeRate - metrics.violentCrimeRate) / nationalAvg.violentCrimeRate * 100).toFixed(0)}% lower` : 
                            `${((metrics.violentCrimeRate - nationalAvg.violentCrimeRate) / nationalAvg.violentCrimeRate * 100).toFixed(0)}% higher`} than the national average
                    </div>
                </div>
                
                {/* Political Representation */}
                {govData && (
                    <div style={chartCardStyle}>
                        <h3 style={{ 
                            fontSize: '16px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: '#333'
                        }}>
                            Political Leadership
                        </h3>
                        {govData.governor && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                                    Governor
                                </div>
                                <div style={{ fontSize: '15px', fontWeight: '500' }}>
                                    {govData.governor.party === 'D' ? 'Democrat' : 
                                     govData.governor.party === 'R' ? 'Republican' : 'Independent'}
                                </div>
                            </div>
                        )}
                        {govData.senators && govData.senators.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                                    Senate Representation
                                </div>
                                <PartyDistribution 
                                    data={[
                                        { 
                                            label: 'Democrat',
                                            count: govData.senators.filter(s => s.party === 'D').length
                                        },
                                        { 
                                            label: 'Republican',
                                            count: govData.senators.filter(s => s.party === 'R').length
                                        },
                                        { 
                                            label: 'Independent',
                                            count: govData.senators.filter(s => s.party === 'I').length
                                        }
                                    ]}
                                />
                            </div>
                        )}
                        {govData.ageStatistics && (
                            <div style={{ 
                                padding: '12px',
                                backgroundColor: '#F8F9FA',
                                borderRadius: '4px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>
                                    {govData.ageStatistics.averageAge} years
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    Average Age of Officials
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Cost & Quality of Life */}
                <div style={chartCardStyle}>
                    <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Cost & Quality of Life
                    </h3>
                    
                    {/* Cost of Living Index */}
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                            Cost of Living (US average = 100)
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ 
                                flex: 1,
                                height: '24px',
                                backgroundColor: '#F0F0F0',
                                borderRadius: '4px',
                                position: 'relative'
                            }}>
                                <div style={{
                                    width: `${Math.min(metrics.costOfLivingIndex, 150)}%`,
                                    height: '100%',
                                    backgroundColor: metrics.costOfLivingIndex < 100 ? COLORS.success : 
                                                     metrics.costOfLivingIndex < 110 ? COLORS.warning : COLORS.secondary,
                                    borderRadius: '4px'
                                }} />
                                <div style={{
                                    position: 'absolute',
                                    left: '66.7%',
                                    top: 0,
                                    bottom: 0,
                                    width: '2px',
                                    backgroundColor: '#333',
                                    opacity: 0.3
                                }} />
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: '600', minWidth: '35px' }}>
                                {metrics.costOfLivingIndex}
                            </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                            {metrics.costOfLivingIndex < 100 ? 'Below average cost' : 
                             metrics.costOfLivingIndex < 110 ? 'Near average cost' : 'Above average cost'}
                        </div>
                    </div>
                    
                    {/* Tax Rates */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ 
                            padding: '10px',
                            backgroundColor: '#F8F9FA',
                            borderRadius: '4px'
                        }}>
                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                {metrics.salesTaxRate.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>Sales Tax</div>
                        </div>
                        <div style={{ 
                            padding: '10px',
                            backgroundColor: '#F8F9FA',
                            borderRadius: '4px'
                        }}>
                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                {metrics.stateIncomeTaxRate.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>Income Tax</div>
                        </div>
                    </div>
                    
                    {/* Quality Indicators */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#F0F8FF', borderRadius: '4px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.primary }}>
                                {metrics.averageCommuteTime.toFixed(0)}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Min Commute</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#F0FFF0', borderRadius: '4px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.success }}>
                                {metrics.broadbandAvailability.toFixed(0)}%
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Broadband</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#FFF5F0', borderRadius: '4px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.secondary }}>
                                {metrics.airQualityIndex.toFixed(0)}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Air Quality</div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Data context footer */}
            <div style={{ 
                marginTop: '24px',
                padding: '12px',
                backgroundColor: '#F8F9FA',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#666',
                textAlign: 'center'
            }}>
                Data sources: US Census Bureau (2021), Bureau of Labor Statistics (2024), CDC (2020), NCES (2022-23)
            </div>
        </div>
    );
};

export default StateDataVisualizations;